import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Button,
    Autocomplete,
    AutocompleteItem,
    DatePicker,
    NumberInput,
    Chip,
    Spinner,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Avatar,
    Select,
    SelectItem,
    Textarea,
} from "@heroui/react";
import {
    LuArrowLeft,
    LuSave,
    LuArrowDownRight,
    LuArrowUpRight,
    LuSearch,
} from "react-icons/lu";
import { useFetchData, usePostData } from "@/hooks";
import { showToast, formatThousands } from "@/utils";
import {
    ThemedText,
    PageHeader,
    EmptyState,
    NoImageFallback,
    ImageLightbox,
} from "@/components/ui";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";

const StockAdjustmentCreate = () => {
    const navigate = useNavigate();
    const { batchId } = useParams();
    const isEdit = !!batchId;

    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(new Set([])); // Multiple filter
    const [searchQuery, setSearchQuery] = useState("");

    // Lightbox state
    const [lightbox, setLightbox] = useState({ isOpen: false, images: [] });

    // For confirmation
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    // Form
    const { handleSubmit, setValue, watch } = useForm({
        defaultValues: {
            date: today(getLocalTimeZone()).toString(),
            description: "",
            items: [], // Will store ALL loaded items
        },
    });

    const watchItems = watch("items");
    const watchDate = watch("date");
    const watchDescription = watch("description");

    // Fetch master data
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");
    const { data: categories } = useFetchData("select/material-category");

    // Fetch existing data for edit
    const {
        data: initialData,
        isLoading: isFetchingEdit,
        error: editError,
        refetch: refetchEdit,
    } = useFetchData(isEdit ? `stock-adjustments/${batchId}/edit` : null);

    // Fetch layout data based on warehouse
    const { data: warehouseStockDetails, isLoading: isLoadingStock } =
        useFetchData(
            selectedWarehouse
                ? `stock-adjustments/warehouse-stock-details?warehouse_id=${selectedWarehouse}&category_id=${Array.from(selectedCategory).join(",")}`
                : null,
        );

    // Mutation
    const endpoint = isEdit
        ? `stock-adjustments/${batchId}/update`
        : "stock-adjustments/store";
    const updateMutation = usePostData(endpoint);

    // Handle Edit Mode Initialization
    useEffect(() => {
        if (isEdit && initialData) {
            // Set warehouse from first item
            if (initialData.items && initialData.items.length > 0) {
                const warehouseId = initialData.items[0].warehouse_id;
                if (warehouseId) {
                    setSelectedWarehouse(String(warehouseId));
                }

                setValue("date", initialData.date);
                setValue("description", initialData.description || "");
            }
        }
    }, [isEdit, initialData, setValue]);

    // Populate Items when warehouse data is loaded
    useEffect(() => {
        if (warehouseStockDetails) {
            const mappedItems = warehouseStockDetails.map((item) => {
                // If editing, try to find existing adjustment for this item to preserve 'new_stock'
                let existingAdj = null;
                if (isEdit && initialData?.items) {
                    existingAdj = initialData.items.find(
                        (adj) =>
                            String(adj.material_id) ===
                                String(item.material_id) &&
                            String(adj.warehouse_id) ===
                                String(selectedWarehouse),
                    );
                }

                // Initial new_stock is undefined (empty) unless editing
                const currentStock = item.current_stock;
                let newStock = undefined;
                let diff = 0;

                if (existingAdj) {
                    // Calculate based on saved diff. Note: live current_stock might have changed since adjustment.
                    const savedDiff =
                        (existingAdj.qty_in || 0) - (existingAdj.qty_out || 0);
                    diff = savedDiff;
                    newStock = currentStock + diff;
                }

                return {
                    ...item,
                    current_stock: currentStock,
                    new_stock: newStock,
                    diff: diff,
                    // Not modified if diff is 0 or newStock is undefined
                    is_modified: diff !== 0,
                };
            });
            setValue("items", mappedItems);
        } else {
            setValue("items", []);
        }
    }, [
        warehouseStockDetails,
        isEdit,
        initialData,
        selectedWarehouse,
        setValue,
    ]);

    // Filter items for table
    const filteredItems = useMemo(() => {
        if (!watchItems) return [];
        return watchItems.filter(
            (item) =>
                item.material
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                item.category.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    }, [watchItems, searchQuery]);

    // Handlers
    const handleNewStockChange = (index, val) => {
        const itemIndex = watchItems.findIndex((i) => i.material_id === index);
        if (itemIndex === -1) return;

        const item = watchItems[itemIndex];
        const newStock = val === "" ? undefined : Number(val); // Handle empty input
        const diff =
            newStock === undefined || newStock === null
                ? 0
                : newStock - item.current_stock;

        const updatedItems = [...watchItems];
        updatedItems[itemIndex] = {
            ...item,
            new_stock: newStock,
            diff: diff,
            is_modified:
                newStock !== undefined && newStock !== null && diff !== 0,
        };
        setValue("items", updatedItems);
    };

    const handleFormSubmit = (data) => {
        // Filter only modified items
        const modifiedItems = data.items.filter((item) => item.is_modified);

        if (modifiedItems.length === 0) {
            showToast(
                "warning",
                "Peringatan",
                "Tidak ada perubahan stok (selisih) yang ditemukan.",
            );
            return;
        }

        const payload = {
            date: data.date,
            description: data.description,
            items: modifiedItems.map((item) => ({
                warehouse_id: selectedWarehouse,
                material_id: item.material_id,
                material_unit_id: item.unit_id,
                current_stock: item.current_stock,
                new_stock: item.new_stock,
                diff: item.diff,
            })),
            _method: isEdit ? "PUT" : undefined,
        };

        setPendingPayload(payload);
        setShowConfirm(true);
    };

    const handleConfirm = () => {
        if (!pendingPayload) return;

        updateMutation.mutate(pendingPayload, {
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    "Penyesuaian stok berhasil disimpan.",
                );
                navigate("/stock-adjustment");
            },
            onError: (e) => {
                showToast(
                    "error",
                    "Penyesuaian stok gagal disimpan.",
                    e.response?.data?.message || "Terjadi kesalahan.",
                );
            },
        });
    };

    if (isEdit && isFetchingEdit) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Spinner label="Memuat data penyesuaian stok..." />
            </div>
        );
    }

    if (isEdit && editError) {
        return (
            <div className="flex h-screen items-center justify-center">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data penyesuaian."
                    onAction={refetchEdit}
                />
            </div>
        );
    }

    return (
        <div className="mx-auto flex h-full max-w-5xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={
                        isEdit
                            ? "Edit Penyesuaian Stok"
                            : "Tambah Penyesuaian Stok"
                    }
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        {
                            label: "Penyesuaian Stok",
                            href: "/stock-adjustment",
                        },
                        { label: isEdit ? "Edit" : "Baru" },
                    ]}
                />
            </div>

            <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="flex flex-col gap-4"
            >
                {/* Control Panel */}
                <Card className="border-default-200 border" shadow="none">
                    <CardBody className="grid grid-cols-1 items-end gap-4 p-4 md:grid-cols-3">
                        <DatePicker
                            label="Tanggal Penyesuaian"
                            value={watchDate ? parseDate(watchDate) : null}
                            onChange={(d) => setValue("date", d.toString())}
                            isRequired
                            variant="bordered"
                            showMonthAndYearPickers
                        />
                        <Autocomplete
                            label="Gudang"
                            placeholder="Pilih Gudang"
                            selectedKey={selectedWarehouse}
                            onSelectionChange={setSelectedWarehouse}
                            isLoading={isLoadingWarehouses}
                            isRequired
                            isDisabled={isEdit}
                            variant="bordered"
                        >
                            {(warehouses || []).map((w) => (
                                <AutocompleteItem
                                    key={String(w.value)}
                                    value={String(w.value)}
                                >
                                    {w.title}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                        <Select
                            label="Filter Kategori (Opsional)"
                            placeholder="Semua Kategori"
                            selectedKeys={selectedCategory}
                            onSelectionChange={setSelectedCategory}
                            selectionMode="multiple"
                            variant="bordered"
                        >
                            {(categories || []).map((c) => (
                                <SelectItem
                                    key={String(c.value)}
                                    value={String(c.value)}
                                >
                                    {c.title}
                                </SelectItem>
                            ))}
                        </Select>
                    </CardBody>
                </Card>

                {/* Main Table Area */}
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/50 flex items-center justify-between px-4 py-3">
                        <Input
                            className="w-full max-w-xs"
                            placeholder="Cari nama produk..."
                            startContent={
                                <LuSearch className="text-default-400" />
                            }
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                            isClearable
                            onClear={() => setSearchQuery("")}
                            size="sm"
                            variant="flat"
                        />
                        {watchItems.length > 0 && (
                            <Chip size="sm" variant="flat" color="secondary">
                                {watchItems.filter((i) => i.is_modified).length}{" "}
                                item disesuaikan
                            </Chip>
                        )}
                    </CardHeader>
                    <CardBody className="min-h-96 p-0">
                        <Table
                            aria-label="Stock List"
                            removeWrapper
                            shadow="none"
                            isHeaderSticky
                            classNames={{
                                base: "max-h-[550px] overflow-y-auto",
                            }}
                        >
                            <TableHeader>
                                <TableColumn>Produk</TableColumn>
                                <TableColumn>Stok Sistem</TableColumn>
                                <TableColumn>Stok Fisik</TableColumn>
                                <TableColumn>Selisih</TableColumn>
                            </TableHeader>
                            <TableBody
                                items={isLoadingStock ? [] : filteredItems}
                                emptyContent={
                                    isLoadingStock ? (
                                        <div className="flex h-96 items-center justify-center">
                                            <Spinner label="Memuat data stok..." />
                                        </div>
                                    ) : !selectedWarehouse ? (
                                        <div className="flex h-96 items-center justify-center">
                                            <EmptyState
                                                title="Pilih Gudang"
                                                description="Silakan pilih gudang terlebih dahulu untuk memulai penyesuaian stok."
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-default-400 flex h-96 items-center justify-center">
                                            Tidak ada produk ditemukan.
                                        </p>
                                    )
                                }
                            >
                                {(item) => (
                                    <TableRow
                                        key={item.material_id}
                                        className={
                                            item.is_modified
                                                ? "bg-primary-50/50 dark:bg-primary-900/20"
                                                : ""
                                        }
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar
                                                    src={
                                                        item.images?.[0]
                                                            ?.url_600
                                                    }
                                                    size="sm"
                                                    radius="full"
                                                    showFallback
                                                    className={
                                                        item.images?.length > 0
                                                            ? "shrink-0 cursor-pointer"
                                                            : "shrink-0"
                                                    }
                                                    onClick={() =>
                                                        item.images?.length >
                                                            0 &&
                                                        setLightbox({
                                                            isOpen: true,
                                                            images: item.images,
                                                        })
                                                    }
                                                    fallback={
                                                        <NoImageFallback />
                                                    }
                                                />
                                                <div className="flex flex-col">
                                                    <ThemedText size="sm">
                                                        {item.material}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                    >
                                                        {item.category}
                                                    </ThemedText>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatThousands(
                                                    item.current_stock,
                                                )}{" "}
                                                {item.unit}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div
                                                className={`max-w-[150px] rounded-lg ${item.is_modified ? "ring-primary ring-2" : ""}`}
                                            >
                                                <NumberInput
                                                    size="sm"
                                                    value={item.new_stock}
                                                    onValueChange={(val) =>
                                                        handleNewStockChange(
                                                            item.material_id,
                                                            val,
                                                        )
                                                    }
                                                    minValue={0}
                                                    aria-label={`Stok fisik ${item.material}`}
                                                    variant="bordered"
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="sm"
                                                variant="flat"
                                                color={
                                                    item.diff > 0
                                                        ? "success"
                                                        : item.diff < 0
                                                          ? "danger"
                                                          : "default"
                                                }
                                                startContent={
                                                    item.diff > 0 ? (
                                                        <LuArrowUpRight />
                                                    ) : item.diff < 0 ? (
                                                        <LuArrowDownRight />
                                                    ) : null
                                                }
                                            >
                                                {item.diff > 0 ? "+" : ""}
                                                {formatThousands(item.diff)}
                                            </Chip>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardBody>
                    <CardFooter className="border-default-200 bg-default-50 flex flex-col items-start justify-between gap-4 border-t px-4 py-3 sm:flex-row sm:items-end">
                        <Textarea
                            className="w-full sm:max-w-md"
                            variant="bordered"
                            placeholder="Keterangan (Opsional)"
                            value={watchDescription}
                            onValueChange={(v) => setValue("description", v)}
                        />
                        <div className="flex w-full justify-end gap-2 sm:w-auto">
                            <Button
                                as={Link}
                                href="/stock-adjustment"
                                variant="light"
                                size="sm"
                            >
                                Batal
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                size="sm"
                                startContent={<LuSave />}
                                isDisabled={
                                    !selectedWarehouse ||
                                    watchItems.filter((i) => i.is_modified)
                                        .length === 0
                                }
                            >
                                Simpan Perubahan
                            </Button>
                        </div>
                    </CardFooter>
                </Card>
            </form>

            {/* Confirmation Modal */}
            <Modal
                isOpen={showConfirm}
                onOpenChange={setShowConfirm}
                scrollBehavior="inside"
                size="xl"
                isDismissable={!updateMutation.isLoading}
                isKeyboardDismissDisabled={updateMutation.isLoading}
                hideCloseButton={updateMutation.isLoading}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>
                                <ThemedText>
                                    Konfirmasi Penyesuaian Stok
                                </ThemedText>
                            </ModalHeader>
                            <ModalBody>
                                <ThemedText size="sm" variant="secondary">
                                    Anda akan menyimpan penyesuaian untuk item
                                    berikut:
                                </ThemedText>
                                <Table
                                    removeWrapper
                                    aria-label="Confirm Table"
                                    className="mt-2"
                                >
                                    <TableHeader>
                                        <TableColumn>Produk</TableColumn>
                                        <TableColumn>Sistem</TableColumn>
                                        <TableColumn>Fisik</TableColumn>
                                        <TableColumn>Diff</TableColumn>
                                    </TableHeader>
                                    <TableBody>
                                        {(pendingPayload?.items || []).map(
                                            (item, idx) => {
                                                const originalItem =
                                                    watchItems.find(
                                                        (i) =>
                                                            i.material_id ===
                                                            item.material_id,
                                                    );
                                                return (
                                                    <TableRow key={idx}>
                                                        <TableCell>
                                                            {
                                                                originalItem?.material
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatThousands(
                                                                originalItem?.current_stock,
                                                            )}{" "}
                                                            {originalItem?.unit}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatThousands(
                                                                item.new_stock,
                                                            )}{" "}
                                                            {originalItem?.unit}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span
                                                                className={
                                                                    item.diff >
                                                                    0
                                                                        ? "text-success"
                                                                        : "text-danger"
                                                                }
                                                            >
                                                                {item.diff > 0
                                                                    ? "+"
                                                                    : ""}
                                                                {formatThousands(
                                                                    item.diff,
                                                                )}
                                                            </span>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            },
                                        )}
                                    </TableBody>
                                </Table>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    size="sm"
                                    variant="light"
                                    onPress={onClose}
                                    isDisabled={updateMutation.isLoading}
                                >
                                    Batal
                                </Button>
                                <Button
                                    size="sm"
                                    color="primary"
                                    onPress={handleConfirm}
                                    isLoading={updateMutation.isLoading}
                                >
                                    Ya, Simpan
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Image Lightbox */}
            <ImageLightbox
                isOpen={lightbox.isOpen}
                onClose={() => setLightbox({ isOpen: false, images: [] })}
                images={lightbox.images}
                urlKey="url_1000"
            />
        </div>
    );
};

export default StockAdjustmentCreate;
